/**
 * Auth Store - Module-level singleton for accessing ApiClient outside React components.
 *
 * TanStack Router loaders run outside the React component tree, so they can't use
 * hooks like useApiClient() or useAuth(). This store provides a bridge:
 *
 * 1. MainAppLayout (React) registers the apiClient on every render
 * 2. Route loaders read the apiClient from the store
 *
 * On first page load, the store is empty (loaders run before layout renders).
 * Loaders handle this gracefully — the component fetches data normally via hooks.
 * On subsequent navigations, the store is populated and loaders can prefetch.
 *
 * NOTE: role/RBAC state is intentionally NOT stored here. Route gating reads
 * the role from RoleContext in-tree (see src/lib/route-role-gate.tsx) — an
 * earlier auth-store-based beforeLoad guard deadlocked on cold loads because
 * the layout that registers the role cannot render until beforeLoad resolves.
 */

import type { ApiClient } from '@/lib/api';

let _apiClient: ApiClient | null = null;

/**
 * Register the current ApiClient instance.
 * Called from MainAppLayout on every render to keep in sync with auth state.
 */
export function registerApiClient(client: ApiClient): void {
  _apiClient = client;
}

/**
 * Get the registered ApiClient, or null if not yet available.
 * Used by route loaders for prefetching.
 */
export function getRegisteredApiClient(): ApiClient | null {
  return _apiClient;
}

let _orgId: string | null = null;

/**
 * Register the current selectedOrgId.
 * Called from MainAppLayout on every render to keep in sync with org selection.
 */
export function registerOrgId(orgId: string | null): void {
  _orgId = orgId;
}

/**
 * Get the registered orgId, or null if not yet available.
 * Used by route loaders for prefetching.
 */
export function getRegisteredOrgId(): string | null {
  return _orgId;
}
